// Tech-agnostic category tags for the news feed and keyword classifier.
// Used by FeedToolbar chip rail + NewsContext client-side filtering.

export const CATEGORY_TAGS = [
  "All",
  "AI",
  "Semiconductors",
  "Big Tech",
  "Startups & VC",
  "Markets",
  "Economy",
  "Crypto",
  "Fintech",
  "Cybersecurity",
  "Robotics",
  "Space",
  "Biotech",
  "Energy & Climate",
  "Quantum",
  "Policy",
] as const;

export type CategoryTag = (typeof CATEGORY_TAGS)[number];

// Case-insensitive keyword sets. An article matches a tag if any of the
// keywords appear in its title, description, or category field.
const KEYWORDS: Record<Exclude<CategoryTag, "All">, string[]> = {
  AI: [
    "artificial intelligence", " ai ", "ai-", "openai", "anthropic", "chatgpt", "gpt-",
    "claude", "gemini", "llm", "large language model", "machine learning", "deep learning",
    "neural network", "copilot", "inference", "agentic", "deepmind", "mistral", "llama",
  ],
  Semiconductors: [
    "semiconductor", "chipmaker", "chips", "nvidia", "amd", "intel", "tsmc", "arm holdings",
    "asml", "gpu", "wafer", "foundry", "fab ", "euv", "hbm", "broadcom", "qualcomm", "micron",
  ],
  "Big Tech": [
    "apple", "microsoft", "google", "alphabet", "amazon", "meta ", "meta's", "tesla", "netflix",
    "antitrust", "iphone", "android", "azure", "aws", "cloud computing", "openai deal",
  ],
  "Startups & VC": [
    "startup", "seed round", "series a", "series b", "series c", "venture capital", "vc firm",
    "raises $", "raised $", "funding round", "y combinator", "valuation", "ipo", "unicorn",
    "acquisition", "acquires", "a16z", "sequoia",
  ],
  Markets: [
    "stocks", "stock market", "s&p 500", "nasdaq", "dow jones", "earnings", "shares fell",
    "shares rose", "bond", "treasury yield", "wall street", "investors", "rally", "selloff",
    "hedge fund", "etf",
  ],
  Economy: [
    "inflation", "federal reserve", "the fed", "interest rate", "rate cut", "gdp", "recession",
    "jobs report", "unemployment", "cpi", "tariff", "trade war", "economy", "consumer spending",
    "central bank",
  ],
  Crypto: [
    "crypto", "bitcoin", "btc", "ethereum", "vitalik", "stablecoin", "usdc", "usdt", "tether",
    "defi", "blockchain", "web3", "token", "onchain", "on-chain", "layer 2", "rollup",
    "arbitrum", "solana", "nft", "coinbase", "binance", "digital asset",
  ],
  Fintech: [
    "fintech", "stripe", "paypal", "plaid", "neobank", "payments", "payment network", "visa",
    "mastercard", "bnpl", "buy now pay later", "digital bank", "remittance", "robinhood",
    "open banking",
  ],
  Cybersecurity: [
    "cybersecurity", "hacker", "hacking", "ransomware", "data breach", "malware", "phishing",
    "vulnerability", "zero-day", "exploit", "cyberattack", "infosec", "cve-", "spyware",
  ],
  Robotics: [
    "robot", "robotics", "humanoid", "boston dynamics", "figure ai", "autonomous vehicle",
    "self-driving", "waymo", "drone", "automation", "cobot",
  ],
  Space: [
    "spacex", "starship", "satellite", "nasa", "rocket", "orbital", "lunar", "mars mission",
    "space station", "blue origin", "rocket lab", "launch vehicle", "astronaut",
  ],
  Biotech: [
    "biotech", "pharma", "clinical trial", "fda approval", "drug", "vaccine", "gene therapy",
    "crispr", "genomics", "obesity drug", "oncology", "biotechnology", "moderna", "pfizer",
  ],
  "Energy & Climate": [
    "climate", "renewable", "solar", "wind farm", "battery", "ev ", "electric vehicle",
    "clean energy", "nuclear", "fusion", "grid", "emissions", "carbon", "oil price", "lithium",
    "data center power",
  ],
  Quantum: ["quantum", "post-quantum", "qubit", "quantum computing", "quantum-safe"],
  Policy: [
    "regulation", "regulator", "sec ", "ftc", "doj", "cftc", "congress", "senate", "white house",
    "lawsuit", "antitrust", "compliance", "enforcement", "sanction", "executive order",
    "eu ai act", "gdpr", "policy", "legislation",
  ],
};

export function articleMatchesTag(
  article: { title?: string | null; description?: string | null; category?: string | null },
  tag: CategoryTag
): boolean {
  if (tag === "All") return true;
  const haystack = `${article.title ?? ""} ${article.description ?? ""} ${article.category ?? ""}`.toLowerCase();
  return KEYWORDS[tag].some((kw) => haystack.includes(kw));
}

export function countArticlesPerTag<T extends { title?: string | null; description?: string | null; category?: string | null }>(
  articles: T[]
): Record<CategoryTag, number> {
  const counts = Object.fromEntries(CATEGORY_TAGS.map((t) => [t, 0])) as Record<CategoryTag, number>;
  counts.All = articles.length;
  for (const article of articles) {
    for (const tag of CATEGORY_TAGS) {
      if (tag === "All") continue;
      if (articleMatchesTag(article, tag)) counts[tag] += 1;
    }
  }
  return counts;
}
